import React from "react";
import AuthContext from "../contexts/AuthContext";
import {useHistory} from "react-router-dom";
import {gql, useMutation} from "@apollo/client";
import jwt_decode from "jwt-decode";
import moment from "moment";
import {useIonAlert} from "@ionic/react";
import {convertMinutesToReadableTime} from "../utils";
import useNotificationModal from "../hooks/useNotificationModal";

let intervalSesionCheck: number | null = null;
let lastUserInteraction: moment.Moment;

const AUTORENEW_SESSION_START = 1000 * 60 * 60 * 1; // 1 hour
const AUTOLOGOUT_SESSION_START = 1000 * 10; // 10 seconds
const TOO_OLD_SESSION_START = 1000 * 60 * 60; // 1 hour

const LOGIN_QUERY = gql`
    mutation login($loginEmail: String!, $loginPassword: String!) {
        login(email: $loginEmail, password: $loginPassword) {
            success
            error
            data {
                token
                refreshToken
                email
            }
        }
    }`;

const REFRESH_QUERY = gql`
    mutation refreshToken($refreshToken: String!) {
        refreshToken(refreshToken: $refreshToken) {
            token
            refreshToken
            user {
                email
            }
        }
    }
`;

const SSO_QUERY = gql`
    mutation Sso($ssoToken: String!) {
        sso(ssoToken: $ssoToken) {
            token
            refreshToken
            user {
                email
            }
        }
    }
`;

const AuthProvider = ({children}: any) => {
    const [presentAlert, dismissAlert] = useIonAlert();
    const [token, setToken] = React.useState<string | undefined>(
        localStorage.getItem('token') || undefined
    );
    const [refreshToken, setRefreshToken] = React.useState<string | undefined>(
        localStorage.getItem('refreshToken') || undefined
    );
    const [email, setEmail] = React.useState<string | undefined>(
        localStorage.getItem('email') || undefined
    );
    const [isSSO, setIsSSO] = React.useState<boolean>(
        localStorage.getItem('isSSO') === 'true'
    );
    const [claims, setClaims] = React.useState({
        userId: null,
        roles: [],
        rules: []
    });
    const [tokenExpiration, setTokenExpiration] = React.useState<number | undefined | null>(
        localStorage.getItem('tokenExpiration') ? Number(localStorage.getItem('tokenExpiration')) : undefined
    );
    const [loginMutation, {data, loading, error}] = useMutation(LOGIN_QUERY);
    const [refreshMutation, {data: refreshData, loading: refreshLoading, error: refreshError}] = useMutation(REFRESH_QUERY);
    const [ssoMutation, {data: ssoData, loading: ssoLoading, error: ssoError}] = useMutation(SSO_QUERY);
    const {showSuccessModal, showErrorModal} = useNotificationModal();
    const history = useHistory();

    const mouseEventListener = () => {
        return (e: MouseEvent) => {
            lastUserInteraction = moment();
        }
    };

    const [onMouseDownEvent] = React.useState(mouseEventListener);

    React.useEffect(() => {
        if (token) {
            localStorage.setItem('token', token);
            setClaims(jwt_decode(token));
        } else {
            localStorage.removeItem('token');
        }
    }, [token]);
    React.useEffect(() => {
        if (isSSO) {
            const ssoSearchParams = new URLSearchParams(window.location.search).get("sso");

            if (isSSO && !ssoSearchParams) {
                setIsSSO(false);
                localStorage.setItem('isSSO', isSSO.toString());
                handleLogout();
            } else {
                localStorage.setItem('isSSO', isSSO.toString());
            }
        } else {
            localStorage.removeItem('isSSO');
        }
    }, [isSSO]);

    React.useEffect(() => {
        if (email) {
            localStorage.setItem('email', email);
        } else {
            localStorage.removeItem('email');
        }
    }, [email]);

    React.useEffect(() => {
        if (refreshToken) {
            localStorage.setItem('refreshToken', refreshToken);
        } else {
            localStorage.removeItem('refreshToken');
        }
    }, [refreshToken]);

    React.useEffect(() => {

        const ssoSearchParams = new URLSearchParams(window.location.search).get("sso");
        if (window.location.pathname.startsWith('/reset')) {
            return;
        }
        if (tokenExpiration) {
            localStorage.setItem('tokenExpiration', tokenExpiration.toString());
        } else {
            localStorage.removeItem('tokenExpiration');
        }

        if (tokenExpiration) {
            if (intervalSesionCheck) {
                clearInterval(intervalSesionCheck);
            }
            checkTokenTimeout(new Date(tokenExpiration));
            intervalSesionCheck = window.setInterval(checkTokenTimeout, 1000 * 5, new Date(tokenExpiration));
        } else {
            if (token) {
                presentAlert("Session expired. Please login again.");
            }
            if (!ssoSearchParams) {
                history.push("/");

            }
        }


    }, [tokenExpiration]);

    const checkTokenTimeout = async (tokenExpiration: Date) => {
        const now = moment();
        const momentExpiration = moment(tokenExpiration);
        // *** expired *** //
        if (tokenExpiration && now.diff(momentExpiration) > TOO_OLD_SESSION_START) { // over an hour expired
            setToken(undefined); // Clear the token or handle other logout logic
            setRefreshToken(undefined);
            setEmail(undefined);
            setTokenExpiration(undefined);
            history.push('/');
            if (intervalSesionCheck) {
                clearInterval(intervalSesionCheck);
            }
        } else if (momentExpiration && momentExpiration.diff(now) < AUTOLOGOUT_SESSION_START) { // check if token is expired
            setToken(undefined); // Clear the token or handle other logout logic
            setRefreshToken(undefined);
            setEmail(undefined);
            setTokenExpiration(undefined);
            history.push('/');
            if (intervalSesionCheck) {
                clearInterval(intervalSesionCheck);
            }
            await dismissAlert();
            await presentAlert({
                header: 'Session Logout',
                subHeader: 'You have been logged out due to session expiration.',
                buttons: ['OK'],
            });
        } else if (lastUserInteraction && now.diff(lastUserInteraction) < 1000 * 60 * 10 && momentExpiration && momentExpiration.diff(now) < AUTORENEW_SESSION_START) { // 1hour before expiration, if they have been active in the last 10 min, go ahead and refresh for them
            const ret = await refreshMutation({variables: {refreshToken}});
            setEmail(ret.data.refreshToken.user.email);
            setToken(ret.data.refreshToken.token);
            setRefreshToken(ret.data.refreshToken.refreshToken);
            if (intervalSesionCheck) {
                clearInterval(intervalSesionCheck);
            }
            let expiration = moment();
            expiration.add(60 * 12, 'minute');
            setTokenExpiration(expiration.valueOf());
            lastUserInteraction = moment();
            intervalSesionCheck = window.setInterval(checkTokenTimeout, 1000 * 5, tokenExpiration);
        }

        let elements = document.getElementsByClassName("sessionExpiration");

        let batteryIcon = '';
        let minsLeft = Math.floor(moment(tokenExpiration).diff(moment()) / (1000 * 60));
        if (minsLeft / 720 > .75) {
            batteryIcon = '<ion-icon icon="data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' class=\'ionicon\' viewBox=\'0 0 512 512\'><rect x=\'32\' y=\'144\' width=\'400\' height=\'224\' rx=\'45.7\' ry=\'45.7\' stroke-linecap=\'square\' stroke-miterlimit=\'10\' class=\'ionicon-fill-none ionicon-stroke-width\'/><rect x=\'85.69\' y=\'198.93\' width=\'292.63\' height=\'114.14\' rx=\'4\' ry=\'4\' stroke-linecap=\'square\' stroke-miterlimit=\'10\' class=\'ionicon-stroke-width\'/><path stroke-linecap=\'round\' stroke-miterlimit=\'10\' d=\'M480 218.67v74.66\' class=\'ionicon-fill-none ionicon-stroke-width\'/></svg>" class="sc-ion-label-md md" role="img" style="color: black;"></ion-icon>';
        } else if (minsLeft / 720 <= .75 && minsLeft / 720 > .5) {
            batteryIcon = '<ion-icon icon="data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' class=\'ionicon\' viewBox=\'0 0 512 512\'><rect x=\'32\' y=\'144\' width=\'400\' height=\'224\' rx=\'45.7\' ry=\'45.7\' stroke-linecap=\'square\' stroke-miterlimit=\'10\' class=\'ionicon-fill-none ionicon-stroke-width\'/><rect x=\'85.69\' y=\'198.93\' width=\'154.31\' height=\'114.13\' rx=\'4\' ry=\'4\' stroke-linecap=\'square\' stroke-miterlimit=\'10\' class=\'ionicon-stroke-width\'/><path stroke-linecap=\'round\' stroke-miterlimit=\'10\' d=\'M480 218.67v74.66\' class=\'ionicon-fill-none ionicon-stroke-width\'/></svg>" class="sc-ion-label-md md" role="img" style="color: black;"></ion-icon>';
        } else {
            batteryIcon = '<ion-icon icon="data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' class=\'ionicon\' viewBox=\'0 0 512 512\'><rect x=\'31\' y=\'144\' width=\'400\' height=\'224\' rx=\'45.7\' ry=\'45.7\' stroke-linecap=\'square\' stroke-miterlimit=\'10\' class=\'ionicon-fill-none ionicon-stroke-width\'/><path stroke-linecap=\'round\' stroke-miterlimit=\'10\' d=\'M479 218.67v74.66\' class=\'ionicon-fill-none ionicon-stroke-width\'/></svg>" role="img" class="md" style="color: black;"></ion-icon>';
        }
        for (let i = 0; i < elements.length; i++) {
            let element = elements.item(i);
            if (element) {
                element.innerHTML = batteryIcon;
                element.setAttribute('title', 'Session Expiration: ' + convertMinutesToReadableTime(minsLeft));
            }
        }

    };

    const handleLogin = async (user: string, password: string) => {
        setIsSSO(false);
        setEmail(undefined);
        setToken(undefined);
        setRefreshToken(undefined);
        const loginResults = (await loginMutation({variables: {loginEmail: user, loginPassword: password}}))?.data?.login;
        if (!loginResults?.success) {
            await showErrorModal(loginResults?.error ?? 'An unknown error occurred');
            return;
        }
        setEmail(loginResults.data.email);
        setToken(loginResults.data.token);
        setRefreshToken(loginResults.data.refreshToken);
        if (intervalSesionCheck) {
            clearInterval(intervalSesionCheck);
        }
        let expiration = moment();
        expiration.add(60 * 12, 'minute');
        setTokenExpiration(expiration.valueOf());
        lastUserInteraction = moment();
        intervalSesionCheck = window.setInterval(checkTokenTimeout, 1000 * 5, tokenExpiration);
        history.push("/announcements");
    };

    const handleLogout = () => {
        setToken(undefined);
        setEmail(undefined);
        setIsSSO(false);
        setTokenExpiration(undefined);
        setRefreshToken(undefined);
    };

    const handleSSO = async (ssoToken: string) => {
        const ret = await ssoMutation({variables: {ssoToken}});
        setEmail(ret.data.sso.user.email);
        setToken(ret.data.sso.token);
        if (intervalSesionCheck) {
            clearInterval(intervalSesionCheck);
        }
        let expiration = moment();
        expiration.add(60 * 12, 'minute');
        setTokenExpiration(expiration.valueOf());
        lastUserInteraction = moment();
        intervalSesionCheck = window.setInterval(checkTokenTimeout, 1000 * 5, tokenExpiration);
        setIsSSO(true);
    }

    const value = {
        loading,
        token,
        tokenExpiration,
        email,
        claims,
        isSSO,
        onLogin: handleLogin,
        onLogout: handleLogout,
        onSSO: handleSSO,
        onMouseDownEvent: onMouseDownEvent,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;